/**
 * Push notifications (feature 'push'; Firebase Cloud Messaging through a PushSender, src/server/node/fcm.ts). Opt-in
 * only: a device is registered when the player turned notifications on in the game (Android 13+ also asks for the
 * system permission), and unregistered when it turns them off, signs out or deletes the account.
 *
 *  - push.register { token, platform, lang }: the device's registration token for the account (C.pushTokens, id =
 *    keyed hash of the token; the token itself is kept: it is the address the sender needs, never logged or answered),
 *    bound to the device session that registered it. A token of another account moves to this one (the device changed
 *    hands); at most config.push.maxDevicesPerAccount per account (the least recently registered goes).
 *  - sessionsEnded: when that device session ends in any way (a sign-out, a sign-in elsewhere replaced it, revoked, a
 *    password reset, expired), its tokens are dropped: a device that is no longer signed in to the account gets none
 *    of its notifications (the app registers again after its next sign-in when the player's choice is on).
 *  - push.unregister { token? }: one token, or every token of the account.
 *  - push.test: a test notification to the account's devices (config.push.testIntervalMs apart).
 *  - notify(playerId, message): what game features call to notify a player (none does yet: docs/integrations-setup.md
 *    names the candidates); a token the sender reports as unregistered is dropped.
 * The account deletion removes the account's tokens (deletionOps). The tokens of a player are found through an index in
 * memory (player -> token hashes: one scan of C.pushTokens on first use, then kept by register), so an ended session
 * or a notification reads only that player's records, never the whole collection.
 */
import { BackendError } from '../../../shared/protocol/errors';
import type { MessageLanguage, PlayerId, PushDevicesResult } from '../../../shared/protocol/model';
import { type StorageBatchOp, deleteOp, putOp } from '../storage';
import type { AccountAudit } from './account-audit';
import { C, type ServiceDeps } from './deps';

export interface PushMessage {
  title: string;
  body: string;
  /** Data for the app (string values, FCM data payload). */
  data?: Record<string, string>;
}

/** Sends one notification to one registration token (src/server/node/fcm.ts). */
export interface PushSender {
  readonly provider: string;
  /** 'sent', or 'unregistered' for a token the service no longer knows (it is dropped). Throws on other failures. */
  send(token: string, message: PushMessage): Promise<'sent' | 'unregistered'>;
}

/** A registered device (C.pushTokens, id = keyed hash of the token). */
export interface PushTokenRecord {
  hash: string;
  playerId: PlayerId;
  token: string;
  platform: 'android' | 'ios' | 'web';
  lang: MessageLanguage | null;
  /** The device session that registered it (services/auth.ts); null = a session-less (in-process) caller. */
  sessionId: string | null;
  createdAt: number;
  updatedAt: number;
}

const TEST_TEXTS: Record<MessageLanguage, PushMessage> = {
  uk: { title: 'FWP', body: 'Сповіщення працюють!' },
  en: { title: 'FWP', body: 'Notifications work!' },
  ru: { title: 'FWP', body: 'Уведомления работают!' },
};

function indexAdd(byPlayer: Map<PlayerId, Set<string>>, playerId: PlayerId, hash: string): void {
  let hashes = byPlayer.get(playerId);
  if (!hashes) byPlayer.set(playerId, (hashes = new Set()));
  hashes.add(hash);
}

export class PushService {
  private readonly lastTest = new Map<PlayerId, number>();
  /**
   * Token hashes per player (C.pushTokens is the truth: tokensOf re-reads each record and keeps only the player's).
   * Only register stores a token, and it adds the hash here, so the index never misses one; it may still name a token
   * that moved to another account or went (the stale hash is dropped when its owner's tokens are next read).
   */
  private index: Promise<Map<PlayerId, Set<string>>> | null = null;

  constructor(
    private readonly d: ServiceDeps,
    private readonly audit: AccountAudit,
    private readonly hashOf: (token: string) => string,
    private readonly sender: PushSender | null,
  ) {}

  get enabled(): boolean {
    return this.sender !== null;
  }

  private loadIndex(): Promise<Map<PlayerId, Set<string>>> {
    this.index ??= (async () => {
      const byPlayer = new Map<PlayerId, Set<string>>();
      for (const t of await this.d.storage.list<PushTokenRecord>(C.pushTokens)) if (t) indexAdd(byPlayer, t.playerId, t.hash);
      return byPlayer;
    })().catch((err) => {
      this.index = null;
      throw err;
    });
    return this.index;
  }

  /** An operator restored a damaged token record (admin server.repairDocument): the index, built without it, has it. */
  async repaired(t: PushTokenRecord): Promise<void> {
    if (t && typeof t.playerId === 'string' && typeof t.hash === 'string') indexAdd(await this.loadIndex(), t.playerId, t.hash);
  }

  private async tokensOf(playerId: PlayerId): Promise<PushTokenRecord[]> {
    const hashes = (await this.loadIndex()).get(playerId);
    if (!hashes) return [];
    const out: PushTokenRecord[] = [];
    for (const hash of [...hashes]) {
      const t = await this.d.storage.get<PushTokenRecord>(C.pushTokens, hash);
      if (t?.playerId === playerId) out.push(t);
    }
    return out;
  }

  /** Drops hashes the player no longer owns (under the player's 'push:' lock: no register of its runs meanwhile). */
  private async prune(playerId: PlayerId): Promise<void> {
    const byPlayer = await this.loadIndex();
    const hashes = byPlayer.get(playerId);
    if (!hashes) return;
    for (const hash of [...hashes]) if ((await this.d.storage.get<PushTokenRecord>(C.pushTokens, hash))?.playerId !== playerId) hashes.delete(hash);
    if (hashes.size === 0) byPlayer.delete(playerId);
  }

  async register(playerId: PlayerId, sessionId: string | null, p: { token: string; platform: PushTokenRecord['platform']; lang?: MessageLanguage }): Promise<PushDevicesResult> {
    const hash = this.hashOf(`push:${p.token}`);
    return this.d.mutex.run(`push:${playerId}`, async () => {
      const now = this.d.clock.now();
      const prev = await this.d.storage.get<PushTokenRecord>(C.pushTokens, hash);
      const rec: PushTokenRecord = {
        hash,
        playerId,
        token: p.token,
        platform: p.platform,
        lang: p.lang ?? null,
        sessionId,
        createdAt: prev?.playerId === playerId ? prev.createdAt : now,
        updatedAt: now,
      };
      const others = (await this.tokensOf(playerId)).filter((t) => t.hash !== hash).sort((a, b) => a.updatedAt - b.updatedAt);
      const ops: StorageBatchOp[] = [putOp(C.pushTokens, hash, rec)];
      while (others.length >= this.d.config.push.maxDevicesPerAccount) ops.push(deleteOp(C.pushTokens, others.shift()!.hash));
      await this.d.storage.batch(ops);
      indexAdd(await this.loadIndex(), playerId, hash);
      await this.prune(playerId);
      await this.audit.record({ event: 'push_registered', playerId, ok: true, details: { platform: p.platform, moved: Boolean(prev && prev.playerId !== playerId) } });
      return { devices: others.length + 1 };
    });
  }

  async unregister(playerId: PlayerId, token: string | undefined): Promise<PushDevicesResult> {
    return this.d.mutex.run(`push:${playerId}`, async () => {
      const mine = await this.tokensOf(playerId);
      const hash = token === undefined ? null : this.hashOf(`push:${token}`);
      const gone = mine.filter((t) => hash === null || t.hash === hash);
      if (gone.length > 0) await this.d.storage.batch(gone.map((t) => deleteOp(C.pushTokens, t.hash)));
      await this.prune(playerId);
      if (gone.length > 0) await this.audit.record({ event: 'push_unregistered', playerId, ok: true, details: { devices: gone.length } });
      return { devices: mine.length - gone.length };
    });
  }

  /** Ended device sessions of the player (AuthService.onSessionsEnded): their devices are unregistered. */
  async sessionsEnded(playerId: PlayerId, sessionIds: readonly string[], reason: string): Promise<void> {
    // Without a sender no device was registered (push.register is refused).
    if (!this.sender) return;
    const ended = new Set(sessionIds);
    await this.d.mutex.run(`push:${playerId}`, async () => {
      const gone = (await this.tokensOf(playerId)).filter((t) => t.sessionId !== null && ended.has(t.sessionId));
      if (gone.length === 0) return;
      await this.d.storage.batch(gone.map((t) => deleteOp(C.pushTokens, t.hash)));
      await this.prune(playerId);
      await this.audit.record({ event: 'push_unregistered', playerId, ok: true, details: { devices: gone.length, sessionEnded: reason } });
    });
  }

  /** Sends a message to every device of the player; returns how many got it. Unregistered tokens are dropped. */
  async notify(playerId: PlayerId, message: PushMessage | ((lang: MessageLanguage) => PushMessage)): Promise<number> {
    const sender = this.sender;
    if (!sender) return 0;
    let sent = 0;
    for (const t of await this.tokensOf(playerId)) {
      const m = typeof message === 'function' ? message(t.lang ?? 'uk') : message;
      try {
        if ((await sender.send(t.token, m)) === 'sent') sent++;
        else await this.d.storage.batch([deleteOp(C.pushTokens, t.hash)]);
      } catch (err) {
        this.d.logger.warn('push notification not sent', { playerId, provider: sender.provider, err: err instanceof Error ? err.message.slice(0, 200) : String(err) });
      }
    }
    return sent;
  }

  /** push.test (at most one per config.push.testIntervalMs). */
  async test(playerId: PlayerId): Promise<{ sent: number }> {
    if (!this.sender) throw new BackendError('FORBIDDEN', 'Push notifications are not configured on this server', { reason: 'feature_disabled', feature: 'push' });
    const now = this.d.clock.now();
    const last = this.lastTest.get(playerId) ?? -Infinity;
    const wait = last + this.d.config.push.testIntervalMs - now;
    if (wait > 0) throw new BackendError('RATE_LIMITED', 'One test notification per minute', { reason: 'push_test_limit', retryAfterMs: wait });
    this.lastTest.set(playerId, now);
    const devices = (await this.tokensOf(playerId)).length;
    const sent = devices > 0 ? await this.notify(playerId, (lang) => TEST_TEXTS[lang]) : 0;
    if (devices > 0 && sent === 0 && (await this.tokensOf(playerId)).length > 0) {
      throw new BackendError('UNAVAILABLE', 'The push service could not be asked right now', { reason: 'push_unavailable' });
    }
    return { sent };
  }

  /** The player's devices (account deletion). */
  async deletionOps(playerId: PlayerId): Promise<StorageBatchOp[]> {
    this.lastTest.delete(playerId);
    return (await this.tokensOf(playerId)).map((t) => deleteOp(C.pushTokens, t.hash));
  }
}
